// Progress Tracker Utility
// Backend-persisted with localStorage cache for instant UI updates

import { getActivityProgressAPI, updateSlideProgress, updateQuizProgress } from '../services/api';

const STORAGE_KEY = 'vae_user_progress';

// Get userId from localStorage (set during login)
const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.id || null;
  } catch { return null; }
};

// Local cache read/write
const getCache = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch { return {}; }
};

const setCache = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
};

// Load progress from backend and populate cache
export const loadProgressFromBackend = async () => {
  const userId = getUserId();
  if (!userId) return {};
  try {
    const data = await getActivityProgressAPI(userId);
    setCache(data);
    return data;
  } catch {
    return getCache();
  }
};

// Mark a slide as viewed for a specific activity
export const markSlideViewed = (activityId, slideIndex, totalSlides) => {
  const data = getCache();

  if (!data[activityId]) {
    data[activityId] = {
      viewedSlides: [],
      totalSlides,
      quizCompleted: false,
      quizScore: null,
      lastViewedSlide: 0,
      startedAt: new Date().toISOString(),
      lastAccessedAt: new Date().toISOString()
    };
  }

  if (!data[activityId].viewedSlides.includes(slideIndex)) {
    data[activityId].viewedSlides.push(slideIndex);
  }
  data[activityId].lastViewedSlide = slideIndex;
  data[activityId].lastAccessedAt = new Date().toISOString();
  data[activityId].totalSlides = totalSlides;

  setCache(data);

  // Fire-and-forget API call
  const userId = getUserId();
  if (userId) {
    updateSlideProgress(userId, activityId, slideIndex, totalSlides).catch(() => {});
  }

  return data[activityId];
};

// Mark quiz as completed with score
export const markQuizCompleted = (activityId, score, totalQuestions) => {
  const data = getCache();

  if (!data[activityId]) {
    data[activityId] = {
      viewedSlides: [],
      totalSlides: 0,
      quizCompleted: false,
      quizScore: null,
      lastViewedSlide: 0,
      startedAt: new Date().toISOString(),
      lastAccessedAt: new Date().toISOString()
    };
  }

  data[activityId].quizCompleted = true;
  data[activityId].quizScore = score;
  data[activityId].quizTotalQuestions = totalQuestions;
  data[activityId].quizCompletedAt = new Date().toISOString();
  data[activityId].lastAccessedAt = new Date().toISOString();

  setCache(data);

  // Fire-and-forget API call
  const userId = getUserId();
  if (userId) {
    updateQuizProgress(userId, activityId, score, totalQuestions).catch(() => {});
  }

  return data[activityId];
};

// Get progress for a specific activity
export const getActivityProgress = (activityId) => {
  const data = getCache();
  return data[activityId] || null;
};

// Calculate progress percentage for an activity
export const getActivityProgressPercent = (activityId) => {
  const progress = getActivityProgress(activityId);
  if (!progress || !progress.totalSlides) return 0;
  const slideProgress = (progress.viewedSlides.length / progress.totalSlides) * 100;
  return Math.min(Math.round(slideProgress), 100);
};

// Check if activity is completed (all slides viewed + quiz done)
export const isActivityCompleted = (activityId) => {
  const progress = getActivityProgress(activityId);
  if (!progress) return false;
  const allSlidesViewed = progress.viewedSlides.length >= progress.totalSlides;
  return allSlidesViewed && progress.quizCompleted;
};

// Get all progress data for dashboard display
export const getAllProgress = () => {
  return getCache();
};

// Get progress summary for a program
export const getProgramProgress = (activityIds) => {
  const data = getCache();
  let totalSlides = 0;
  let viewedSlides = 0;
  let completedActivities = 0;
  let totalQuizScore = 0;
  let completedQuizzes = 0;

  activityIds.forEach(activityId => {
    const progress = data[activityId];
    if (progress) {
      totalSlides += progress.totalSlides || 0;
      viewedSlides += progress.viewedSlides?.length || 0;
      if (isActivityCompleted(activityId)) completedActivities++;
      if (progress.quizCompleted && progress.quizScore !== null) {
        totalQuizScore += progress.quizScore;
        completedQuizzes++;
      }
    }
  });

  return {
    totalActivities: activityIds.length,
    completedActivities,
    progressPercent: totalSlides > 0 ? Math.round((viewedSlides / totalSlides) * 100) : 0,
    averageQuizScore: completedQuizzes > 0 ? Math.round(totalQuizScore / completedQuizzes) : null,
    completedQuizzes
  };
};

// Reset progress for an activity
export const resetActivityProgress = (activityId) => {
  const data = getCache();
  if (data[activityId]) {
    data[activityId] = {
      viewedSlides: [],
      totalSlides: data[activityId].totalSlides,
      quizCompleted: false,
      quizScore: null,
      lastViewedSlide: 0,
      startedAt: new Date().toISOString(),
      lastAccessedAt: new Date().toISOString()
    };
    setCache(data);
  }
};

// Clear all progress
export const clearAllProgress = () => {
  localStorage.removeItem(STORAGE_KEY);
};

export default {
  markSlideViewed,
  markQuizCompleted,
  getActivityProgress,
  getActivityProgressPercent,
  isActivityCompleted,
  getAllProgress,
  getProgramProgress,
  resetActivityProgress,
  clearAllProgress,
  loadProgressFromBackend
};
